<!--
  =====================================================================
  PRODUCTS DETAIL PAGE - PRODUCT SPECIFICATION VIEW
  =====================================================================
  
  Displays detailed product information with comprehensive content sections.
  
  FEATURES:
  - Dynamic product lookup from URL parameters (macro-area/category/product)
  - Multi-image carousel with smart placeholder detection
  - Responsive grid layout with multiple section types
  - Back navigation to parent category
  - Dynamic category name resolution
  - Handles both product store data and category card data
  - Carousel resets on product change
  - Back button visibility management
  - Multiple content section types (center, tables, lists, grids, etc.)
  - Shows loading/error/not-found states
  
  URL PATTERNS:
  - /cassa/prodotti/:macroArea/:category/:product
  - Direct access pages at /cassa/prodotti/links/direct-access/:product
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <main class="product-detail">
      <div class="product-back-top" v-show="!isMegamenuOpen && !isFooterButtonVisible && !isDirectAccessPage">
        <a :href="categoryBackLink" class="back-to-category">
          <span class="arrow">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="label">{{ categoryBackText }}</span>
        </a>
      </div>

      <div v-if="state.loading" class="loading-state">Caricamento…</div>
      
      <div v-else-if="state.error" class="error-state">Errore nel caricamento</div>
      
      <div v-else>
        <div class="product-header">
          <p class="product-eyebrow" v-if="productEyebrow">{{ productEyebrow }}</p>
          <h1 class="product-title">{{ productTitle }}</h1>
        </div>

        <div v-if="shouldShowCarousel" class="product-image-carousel">
          <div v-if="isMultipleImages" class="carousel-container">
            <button 
              v-show="carouselIndex > 0"
              class="carousel-arrow carousel-arrow-left" 
              @click="carouselIndex--"
              aria-label="Immagine precedente"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="carousel-image-wrapper">
              <img 
                :src="currentCarouselImage" 
                :alt="productTitle" 
                class="carousel-image"
              />
            </div>

            <button 
              v-show="carouselIndex < productImages.length - 1"
              class="carousel-arrow carousel-arrow-right" 
              @click="carouselIndex++"
              aria-label="Immagine successiva"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div v-else class="single-image-wrapper">
            <img 
              :src="currentCarouselImage" 
              :alt="productTitle" 
              class="carousel-image"
            />
          </div>

          <div v-if="isMultipleImages" class="carousel-dots">
            <button
              v-for="(_, index) in productImages"
              :key="index"
              class="dot"
              :class="{ active: carouselIndex === index }"
              @click="carouselIndex = index"
              :aria-label="`Immagine ${index + 1}`"
            />
          </div>
        </div>

        <component
          v-for="(section, index) in enhancedSections"
          :key="index"
          :is="getComponent(section.type)"
          :content="section.content"
        />
      </div>
    </main>

    <ArrowUp />
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
import { state, loadPageContent, setNotFound } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter from '@/components/sections/shared/SiteFooter.vue'

// ───────────────────────────────────────────────────────────────────
// HTML-BASED SECTION COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SectionDocumentHtml from '@/components/sections/html/SectionDocumentHtml.vue'
import HtmlTextSection from '@/components/sections/html/SectionHtmlText.vue'
import ArrowUp from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// COMPONENT STATE
// ───────────────────────────────────────────────────────────────────

const productData = ref(null)
const macroArea = ref(null)
const category = ref(null)
const product = ref(null)
const isMegamenuOpen = ref(false)
const isFooterButtonVisible = ref(false)
const carouselIndex = ref(0)

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES - DATA RESOLUTION
// ───────────────────────────────────────────────────────────────────

/**
 * Enhanced page sections from product data
 * @returns {Array} Sections array from product store
 */
const enhancedSections = computed(() => {
  if (!productData.value?.sections) return []
  return [...productData.value.sections]
})

/**
 * Generates back link to parent category
 * Falls back to main products page if no category context
 * @returns {string} Category URL or products home
 */
const categoryBackLink = computed(() => {
  if (macroArea.value && category.value) {
    return `/cassa/prodotti/${macroArea.value}/${category.value}`
  }
  return '/cassa/prodotti'
})

/**
 * Resolves back button text from category metadata
 * @returns {string} Contextual back button text
 */
const categoryBackText = computed(() => {
  if (state.categorie && macroArea.value && category.value) {
    const categoryData = state.categorie[macroArea.value]?.[category.value]
    return categoryData?.title ? `Torna a ${categoryData.title}` : 'Torna alla categoria'
  }
  return 'Torna ai prodotti'
})

/**
 * Gets product title, defaults to generic label
 * @returns {string} Product title
 */
const productTitle = computed(() => productData.value?.title || 'Prodotto')

/**
 * Resolves product eyebrow (subheading) from category or field
 * @returns {string} Eyebrow text or category name
 */
const productEyebrow = computed(() => {
  if (productData.value?.eyebrow) return productData.value.eyebrow
  const cat = categoryBackText.value
  return cat ? cat.replace('Torna a ', '') : ''
})

/**
 * Identifies if page is direct access page (no back navigation)
 * @returns {boolean} True if in links/direct-access category
 */
const isDirectAccessPage = computed(() => {
  return macroArea.value === 'links' && category.value === 'direct-access'
})

// ───────────────────────────────────────────────────────────────────
// PLACEHOLDER IMAGE DETECTION
// ───────────────────────────────────────────────────────────────────

/**
 * Extracts filename from URL (handles various URL formats)
 * @param {string} url - The image URL
 * @returns {string} Filename without path
 */
function getBasename(url) {
  if (!url) return ''
  const parts = url.split('/')
  return parts[parts.length - 1].split('?')[0]
}

/**
 * Detects if image is a placeholder (generic template image)
 * Matches: placeholder_news, placeholder_product, placeholder_document, etc.
 * 
 * @param {string} src - Image source URL
 * @returns {boolean} True if image is identified as placeholder
 */
function isPlaceholderImage(src) {
  if (!src) return false
  const name = getBasename(src)
  const placeholderRegex = /^placeholder[_-]?(?:news|product|document)(?:_\d+)?\.(png|jpg|jpeg|webp)$/i
  return placeholderRegex.test(name)
}

// ───────────────────────────────────────────────────────────────────
// CAROUSEL IMAGE LOGIC
// ───────────────────────────────────────────────────────────────────

/**
 * Gets normalized product images as array
 * Converts single image to array format for uniform handling
 * @returns {Array} Array of image URLs
 */
const productImages = computed(() => {
  if (!productData.value?.image) return []
  const images = Array.isArray(productData.value.image) 
    ? productData.value.image 
    : [productData.value.image]
  return images
})

/**
 * Determines if carousel should display multiple image mode
 * @returns {boolean} True if 2+ images available
 */
const isMultipleImages = computed(() => {
  return Array.isArray(productImages.value) && productImages.value.length > 1
})

/**
 * Smart carousel visibility control
 * Hides carousel if no valid (non-placeholder) images exist
 * @returns {boolean} True if carousel should display
 */
const shouldShowCarousel = computed(() => {
  if (!Array.isArray(productImages.value) || productImages.value.length === 0) {
    return false
  }
  const allPlaceholders = productImages.value.every(img => isPlaceholderImage(img))
  return !allPlaceholders
})

/**
 * Gets current carousel image at navigation index
 * @returns {string|null} Current image URL or null
 */
const currentCarouselImage = computed(() => {
  if (!Array.isArray(productImages.value) || productImages.value.length === 0) return null
  return productImages.value[carouselIndex.value] || null
})

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps content section types to Vue components
 * Supports both structured and HTML-based sections
 * 
 * @param {string} type - Section type identifier
 * @returns {VueComponent} Component to render section
 */
function getComponent(type) {
  switch (type) {

    case 'section-document-html':   return SectionDocumentHtml
    case 'html-text':               return HtmlTextSection
  }
}

// ───────────────────────────────────────────────────────────────────
// CAROUSEL STATE MANAGEMENT
// ───────────────────────────────────────────────────────────────────

/**
 * Reset carousel index when product changes
 * Ensures fresh carousel state for each product view
 */
watch(productData, () => {
  carouselIndex.value = 0
})

// ───────────────────────────────────────────────────────────────────
// BACK BUTTON VISIBILITY MANAGEMENT
// ───────────────────────────────────────────────────────────────────

/**
 * Checks megamenu and footer visibility to control back button display
 * Hides top back button if footer has visible back button
 */
const checkMegamenuAndFooter = () => {
  const megamenu = document.querySelector('.mega-menu')
  isMegamenuOpen.value = !!megamenu
  
  const footerButton = document.querySelector('footer .back-to-category')
  if (footerButton) {
    const rect = footerButton.getBoundingClientRect()
    isFooterButtonVisible.value = rect.top >= 0 && rect.top <= window.innerHeight
  }
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize page on component mount
 * - Parse URL for product location (macro-area/category/product)
 * - Load product data from store
 * - Normalize image arrays
 * - Setup DOM observers and event listeners
 */
onMounted(async () => {
  // Parse URL: /cassa/prodotti/:macroArea/:category/:product
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  
  let segments = pathSegments
  if (segments[0] === 'cassa') {
    segments = segments.slice(1)
  }
  
  if (segments[0] === 'prodotti') {
    segments = segments.slice(1)
  }
  
  macroArea.value = segments[0]   // e.g. 'links'
  category.value = segments[1]    // e.g. 'direct-access'
  product.value = segments[2]     // e.g. 'esempio_VOox6S'
  
  // Load product index if not already loaded
  if (!state.prodotti || Object.keys(state.prodotti).length === 0) {
    await loadPageContent('prodotti')
  }
  
  // Try to find product in store (by flat key first, then by simple key)
  const flatKey = `${macroArea.value}/${category.value}/${product.value}`
  productData.value = state.prodotti?.[flatKey] || state.prodotti?.[product.value]

  // Fallback: search in category cards
  if (!productData.value && state.categorie) {
    const categoryObj = state.categorie[macroArea.value]?.[category.value]
    if (categoryObj && categoryObj.cards) {
      const card = categoryObj.cards.find(c => c.slug === product.value)
      if (card) {
        productData.value = {
          ...card,
          // Ensure image is always array
          image: card.image ? (Array.isArray(card.image) ? card.image : [card.image]) : null
        }
      }
    }
    
    // If product not found, trigger centralized 404 handling
    if (!productData.value) {
      setNotFound()
      return
    }
  }
  
  // Normalize image array for found product
  if (productData.value && productData.value.image) {
    if (!Array.isArray(productData.value.image)) {
      productData.value.image = [productData.value.image]
    }
  }

  // Setup DOM observer for visibility tracking
  const observer = new MutationObserver(checkMegamenuAndFooter)
  observer.observe(document.body, { 
    childList: true,
    attributes: true, 
    attributeFilter: ['class'],
    subtree: true 
  })
  
  // Setup scroll/resize event listeners
  window.addEventListener('scroll', checkMegamenuAndFooter)
  window.addEventListener('resize', checkMegamenuAndFooter)
  setTimeout(checkMegamenuAndFooter, 100)

  // Cleanup on unmount
  onUnmounted(() => {
    observer.disconnect()
    window.removeEventListener('scroll', checkMegamenuAndFooter)
    window.removeEventListener('resize', checkMegamenuAndFooter)
  })
})
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

/* ------------------------------------------------------------------
   Wrapper
------------------------------------------------------------------ */
.product-detail {
  padding: $space-2 $container-padding-x;
  max-width: 1200px;
  margin: 0 auto;
  color: $clr-brand-gray;
}

/* ------------------------------------------------------------------
   Bottone torna indietro
------------------------------------------------------------------ */
.product-back-top {
  position: fixed;
  top: calc(var(--header-total-height) + 1.2rem);
  left: 1rem;
  z-index: 150;

  .back-to-category {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    line-height: 1;
    padding: 0.6rem 0;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: scale(1.05);
      }

      &:hover .arrow {
        transform: rotate(-90deg);
      }
    }

    .label {
      display: inline-block;
      white-space: nowrap;
      transition: opacity 0.25s ease, transform 0.25s ease;
      will-change: opacity, transform;
    }

    @media (max-width: 1700px) {
      @include flex-gap(0);

      .label {
        opacity: 0;
        transform: translateX(-6px);
        pointer-events: none;
      }
    }
  }

  @media (max-width: 1300px) {
    top: calc(var(--header-total-height) + 1.2rem);
    left: 1rem;
  }

  @media (max-width: 920px) { 
    display: none; 
  }

  @media (max-width: 768px) {
    left: 0;
  }
}

/* ------------------------------------------------------------------
   Header (SENZA IMMAGINE)
------------------------------------------------------------------ */
.product-header {
  margin-bottom: $space-3;

  .product-eyebrow {
    position: relative;
    display: inline-block;
    font-size: $font-eyebrow;
    font-weight: 600;
    font-family: 'Karla', sans-serif!important;
    color: $clr-tertiary;
    text-transform: uppercase;
    letter-spacing: 1px;
    line-height: 1.2;
    margin: 0 0 $space-2 0;
    padding-left: $space-1-5;
    cursor: default;
    white-space: nowrap;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -30px;
      width: 40%;
      height: 12px;
      background-color: rgba($clr-tertiary, 0.7);
      transition: all 0.5s ease;
    }

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;

        &::before {
          background: rgba($clr-tertiary, 0.8);
          width: 120%;
          transform: scaleY(1.2);
        }
      }
    }
  }

  .product-title {
    font-size: $font-title;
    font-weight: 300;
    font-family: 'Karla', sans-serif!important;
    color: $clr-brand-gray;
    line-height: 1.2;
    margin: 0 0 $space-0-25 0;
  }
}

/* Freccia */
.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  height: 1em;
  transition: transform 0.3s ease;
  transform: rotate(90deg);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
}

/* ------------------------------------------------------------------
   Loading / Error
------------------------------------------------------------------ */
.loading-state,
.error-state {
  text-align: center;
  padding: $space-4 $space-1;
  color: $clr-text-light;
  font-size: $font-base;
}

@media (max-width: 768px) {
  .loading-state,
  .error-state {
    padding: $space-3 $space-0-5;
    font-size: $font-base-sm;
  }
}

/* ------------------------------------------------------------------
   Not found
------------------------------------------------------------------ */
.product-not-found {
  text-align: center;
  padding: $space-4 $space-1;

  h1 {
    font-size: $font-title;
    color: $clr-brand-dark;
    margin-bottom: $space-1;
  }

  p {
    font-size: $font-base;
    color: $clr-text-light;
    margin-bottom: $space-2;
  }

  .back-to-category {
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    line-height: 1;
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.25s ease;
    padding: 0.6rem 0;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: translateX(-4px);
      }

      &:hover .arrow {
        transform: rotate(-90deg);
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries - Display medio-grande intermedio (1025px – 1300px)
   Necessario perché main { padding: 0 !important } nel global
   sovrascrive il padding del componente; qui usiamo !important con
   specificità maggiore (selettore di classe) per ripristinarlo.
------------------------------------------------------------------ */
@media (min-width: 1025px) and (max-width: 1300px) {
  .product-detail {
    padding-left: clamp(2rem, 4vw, 3rem) !important;
    padding-right: clamp(2rem, 4vw, 3rem) !important;
  }
}

/* ------------------------------------------------------------------
   Media queries - Display medio-grande (≤1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .product-header {
    .product-eyebrow {
      font-size: $font-eyebrow-lg;
      
      &::before {
        height: 10px;
        left: -25px;
      }
    }

    .product-title {
      font-size: $font-title-lg;
      font-weight: 300;
    }
  }
}

/* ------------------------------------------------------------------
   Tablet / Mobile (≤1024px) — padding laterale con !important
   necessario perché body main { padding: 0 !important } nel global
   sovrascrive il padding del componente; con !important e specificità
   maggiore (.product-detail[data-v-xxx] = 0,2,0 > body main = 0,0,2)
   il componente vince.
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .product-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
    overflow-x: hidden;
  }

  /* Immagine a tutta larghezza (bleed) — bilancia esattamente il padding */
  .product-image-carousel {
    margin-left: -$space-1;
    margin-right: -$space-1;
    width: calc(100% + 2rem);
  }

  .carousel-image {
    width: 100%;
    max-height: 300px;
    box-shadow: none;
  }

  .carousel-arrow {
    &.carousel-arrow-left { left: 0.75rem; }
    &.carousel-arrow-right { right: 0.75rem; }
  }
}

/* ------------------------------------------------------------------
   Media queries - Tablet (≤920px)
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .product-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }

  .product-header {
    .product-eyebrow {
      font-size: $font-eyebrow-sm;
      margin-bottom: $space-1;
      white-space: normal;

      &::before {
        height: 8px;
        left: -20px;
      }
    }

    .product-title {
      font-size: $font-title-sm;
      font-weight: 300;
    }
  }

  .carousel-arrow {
    width: 50px;
    height: 50px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
}

/* ------------------------------------------------------------------
   Mobile (≤ 768 px)
------------------------------------------------------------------ */
@media (max-width: 768px) {
  .product-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }
}

/* ------------------------------------------------------------------
   Mobile (≤480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .product-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }

  .product-header {
    .product-eyebrow {
      font-size: $font-eyebrow-xs;
      margin-bottom: $space-0-5;
      
      &::before {
        height: 7px;
        left: -15px;
      }
    }

    .product-title {
      font-size: $font-title-xs;
      font-weight: 300;
    }
  }

  .product-image-carousel {
    margin-bottom: $space-1-5;
  }
}

/* ------------------------------------------------------------------
   Carosello Immagini
------------------------------------------------------------------ */
.product-image-carousel {
  width: 100%;
  margin: 0 auto $space-3 auto;
  text-align: center;
}

.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  @include flex-gap(1rem);
  width: 100%;
}

.carousel-image-wrapper,
.single-image-wrapper {
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.carousel-image {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 500px;
  display: inline-block;
  box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
  transition: all 0.3s ease;

  @media (min-width: 1025px) and (max-width: 1300px) {
    max-height: 360px;
  }

  @media (hover: hover) {
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
    }
  }
}

.carousel-arrow {
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba($clr-brand-dark, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  flex-shrink: 0;
  outline: none;
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    color: $clr-brand-dark;
  }

  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 1);
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.15);
    }
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }
  /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
     scartata per intero. Si ripiega su :focus, che mostra l'anello
     anche col mouse: e' il degrado accettato. */
  .no-focusvisible &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  &.carousel-arrow-left {
    left: -3rem;
  }

  &.carousel-arrow-right {
    right: -3rem;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    padding: 0;

    svg {
      width: 20px;
      height: 20px;
    }

    &.carousel-arrow-left {
      left: 0.75rem;
    }

    &.carousel-arrow-right {
      right: 0.75rem;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    padding: 0;

    svg {
      width: 18px;
      height: 18px;
    }

    &.carousel-arrow-left {
      left: 0.5rem;
    }

    &.carousel-arrow-right {
      right: 0.5rem;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
  }
}

.carousel-dots {
  display: flex;
  justify-content: center;
  @include flex-gap(0.75rem);
  margin-top: 1.5rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: rgba($clr-text-light, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-text-light, 0.5);
      transform: scale(1.2);
    }
  }

  &.active {
    background-color: $clr-tertiary;
    width: 24px;
    border-radius: 5px;
  }
}

/* ──────────────────────────────────────────────────────────────
   L'eyebrow aveva un padding-left che spostava il TESTO a destra
   lasciando il box dov'era: era quello a disallinearlo dal titolo.
   Qui viene azzerato, cosi' l'eyebrow parte esattamente dove parte
   il titolo. Nessun titolo viene spostato.
   Il ::before e' position:absolute e non dipende dal padding.
   Classe ripetuta tre volte per battere le regole annidate originali.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .product-eyebrow.product-eyebrow.product-eyebrow {
    padding-left: 0;
  }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO AL PARAGRAFO
   eyebrow, titolo e corpo del testo devono partire dalla stessa x,
   quella di "chi siamo": 44px a 390, 39 a 360, 54 a 600, 102 a 768-920,
   122 a 1024. Valori ricavati misurando il primo glifo di ogni elemento.
   Classe ripetuta tre volte per battere le regole annidate originali;
   blocchi in fondo al file perche' l'ordine decide fra breakpoint.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .product-eyebrow.product-eyebrow.product-eyebrow {
    padding-left: 0;
  }
}

@media (max-width: 480px) {
  .product-eyebrow.product-eyebrow.product-eyebrow {
    margin-left: 28px;
    padding-left: 0;
  }

  .product-title.product-title.product-title {
    margin-left: 28px;
  }

  :deep(.html-content) {
    margin-left: 12px;
  }
}

@media (max-width: 360px) {
  .product-eyebrow.product-eyebrow.product-eyebrow {
    margin-left: 23px;
    padding-left: 0;
  }

  .product-title.product-title.product-title {
    margin-left: 23px;
  }

  :deep(.html-content) {
    margin-left: 11px;
  }
}
</style>
